import { useState } from 'react';
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog';
import { BlockButton } from '@/components/BlockButton';
import Select from '@/components/Select';
import CustomFieldInput from '@/components/CustomFieldInput';
import useFetchCandidateProfilesCustomFields from '../api/useFetchCandidateProfilesCustomFields';
import useBulkUpdateCustomFields from '../api/useBulkUpdateCustomFields';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';

type CustomFieldValue = string | number | string[];

type Props = {
  ids: number[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export default function BulkUpdateCustomFieldsModal({ ids, open, onOpenChange, onSuccess }: Props) {
  const [selectedFieldId, setSelectedFieldId] = useState<string>('');
  const [fieldValue, setFieldValue] = useState<CustomFieldValue>('');
  const { data: customFieldsData } = useFetchCandidateProfilesCustomFields();
  const { mutateAsync: bulkUpdate, isPending } = useBulkUpdateCustomFields();

  const customFields = customFieldsData?.records ?? [];
  const fieldItems = customFields.map((field) => ({
    value: String(field.id),
    label: field.name,
  }));

  const selectedField = customFields.find((f) => f.id === Number(selectedFieldId));

  const handleFieldChange = (value: string) => {
    setSelectedFieldId(value);
    setFieldValue('');
  };

  const handleSubmit = async () => {
    if (!selectedFieldId || !fieldValue) return;
    const content = Array.isArray(fieldValue) ? fieldValue.join(',') : String(fieldValue);
    try {
      await bulkUpdate({
        ids,
        custom_data: [{ field_id: Number(selectedFieldId), content }],
      });
      showSuccessToast('データを更新しました');
      resetForm();
      onSuccess();
    } catch {
      showErrorToast('データの更新に失敗しました');
    }
  };

  const resetForm = () => {
    setSelectedFieldId('');
    setFieldValue('');
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const isSubmitDisabled = !selectedFieldId || !fieldValue || isPending;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-[728px]">
        <div className="flex h-[53px] items-center gap-1 border-b border-solid border-border px-6">
          <p className="text-sm font-bold">求職者データの更新</p>
        </div>
        <div className="border-b border-solid border-border p-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-bold">変更する項目を選択</label>
              <Select
                items={fieldItems}
                defaultLabel="項目を選択"
                value={selectedFieldId}
                onValueChange={handleFieldChange}
              />
            </div>
            {selectedField && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-bold">変更後の値を入力</label>
                <CustomFieldInput
                  field={selectedField}
                  value={fieldValue}
                  onChange={setFieldValue}
                  placeholder={`${selectedField.name}を入力`}
                  className="w-full"
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex h-[74px] items-center justify-end gap-4 px-6">
          <div className="w-[94px]">
            <BlockButton type="button" colorType="white" text="キャンセル" onClick={handleClose} />
          </div>
          <div className="w-[108px]">
            <BlockButton
              type="button"
              colorType="blue"
              text="更新する"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            />
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
